/**
 * boardFocus — which single element on the board is lit, and where.
 *
 * ══════════════════════════════════════════════════════════════════════
 * 🔴 THE ATTENTION BATON. Ben, on the glow: "It's essentially showing you where
 * to look." That is the whole specification, and it turns four scattered pieces
 * of UI into one thing:
 *
 *   WAITING              ON THE BLOCK            SOLD               WAITING
 * nominator's card  ->  the nomination card  ->  winner's card  ->  next one
 *                                               + the slot he filled
 *
 * Same visual verb throughout, so the room learns one thing instead of four. A
 * person who looks up mid-draft finds the lit element and is instantly current.
 *
 * 🔴 THE FAILURE MODE THIS EXISTS TO PREVENT: two lit elements disagreeing. If
 * the banner says SOLD while a roster card pulses "your turn", the baton has
 * been dropped and the feature is worse than none — it is two sources telling
 * the room to look in different places.
 *
 * So the precedence is decided HERE, once, and both NominationBlock and
 * RosterBoard render from the answer. NEITHER RE-DERIVES IT. The rule that a
 * nomination outranks a SOLD still on screen used to live as a comment inside
 * NominationBlock; it is the rule, not a note about one component, so it moved
 * here with the code.
 * ══════════════════════════════════════════════════════════════════════
 */
import { nextOpenSeat } from './liveBoard';

/**
 * WHOSE TURN IT IS, guarded — the seat `nominatorIndex` names, or -1 for nobody.
 *
 * 🔴 EXPORTED SO THERE IS EXACTLY ONE ANSWER TO THAT QUESTION. Two surfaces ask
 * it: the baton's bright mark in the WAITING state, and the checked row in the
 * board menu's team list. They were trivially equal — `raw % teams` in one place
 * and a bare `i === board.nominatorIndex` in the other — which is precisely the
 * kind of agreement that survives review and then stops being true the first
 * time somebody touches one of them. Now they cannot disagree, and the guard
 * below applies to both instead of to one.
 *
 * ⚠️ `typeof`, not `Number()`. `Number(null)` is 0 — so a board carrying an
 * explicit "no prediction" as null would coerce straight onto seat 0, which is
 * the same wrong answer the `+1` guard exists to prevent, arriving by a
 * different route. Only an actual number is a seat.
 */
export function turnSeat(board) {
  const teams = board?.teamNames?.length || 0;
  const raw = board?.nominatorIndex;
  return teams > 0 && typeof raw === "number" && Number.isFinite(raw) && raw >= 0
    ? raw % teams
    : -1;
}

/**
 * @param board the live board
 * @param sold  the sale currently flashing, from useSoldFlash — the ONE timer,
 *              owned by the container and passed to both consumers. Two
 *              instances would mean the banner clearing at a different instant
 *              from the card glow, which is a visibly dropped baton.
 *
 * @returns {{ mode, brightTeam, faintTeam, sale }}
 *   mode       "block" | "sold" | "waiting"
 *   brightTeam the seat carrying the bright mark, or -1 for nobody
 *   faintTeam  the seat carrying the faint mark, or -1
 *   sale       the flashing sale in "sold" mode, else null. It identifies a
 *              ROW as well as a card: the filled slot is the one whose player
 *              matches it, which is what answers *which slot* from ten feet.
 *
 * 🔴 EXACTLY ONE BRIGHT SEAT, EVER — and in the on-the-block state, none at
 * all: the nomination card has the baton and a competing roster glow is the
 * dropped-baton failure above.
 *
 * 🔴 `nominatorIndex < 0` IS NOBODY AND MUST SURVIVE THE `+1`. `(-1 + 1) % n`
 * is 0, which silently promotes seat 0 to on deck on every board with no
 * prediction. sanitizeLiveBoard clamps a STORED index into range, so this can
 * only ever arrive from a caller — which is exactly why the guard is here and
 * not at one of the two render sites.
 */
export function boardFocus(board, sold) {
  const teams = board?.teamNames?.length || 0;
  const turnTeam = turnSeat(board);
  /**
   * 🔴 ON DECK SKIPS SEATS THAT ARE DONE, AND THIS IS THE ONE PLACE A READ MAY.
   * The mark above it — `turnSeat` — must not, because a stored index is also
   * what the board menu's checkmark reads and what `setNominator` writes, so a
   * skipping read there would light a different card than the row an operator
   * clicked. On deck has NO stored counterpart and NO menu row: it is a pure
   * prediction derived here and rendered in one place, so there is nothing it
   * can disagree with. The rotation itself skips on WRITE (useLiveBoard), which
   * is why this stays a one-line agreement with it rather than a second rule.
   */
  const next = turnTeam < 0 ? -1 : nextOpenSeat(board, turnTeam);

  // A nomination outranks a SOLD still on screen: the room's attention belongs
  // on the player being bid on now, and the sale that just happened is on the
  // roster cards either way.
  if (board?.nomination) {
    // Nothing bright in the grid. The faint mark sits on `nominatorIndex`,
    // which by now IS the next nominator — the advance happens on nominate,
    // not on the sale — so nothing moves when the block is filled.
    return { mode: "block", brightTeam: -1, faintTeam: turnTeam, sale: null };
  }

  if (sold) {
    const t = Number(sold.team);
    const winner = Number.isFinite(t) && t >= 0 && t < teams ? t : -1;
    // No faint mark during SOLD. For three and a half seconds the board is
    // answering one question — who just got him — and a second mark pointing
    // somewhere else is the thing this function exists to prevent.
    return { mode: "sold", brightTeam: winner, faintTeam: -1, sale: sold };
  }

  // 🔴 THE REVERSAL. Waiting lights the seat we are waiting ON, not the one
  // after it. See the header on RosterBoard for why the banner naming the same
  // person is not the duplication it looks like.
  return { mode: "waiting", brightTeam: turnTeam, faintTeam: next, sale: null };
}

export default boardFocus;
